import { useEffect, useRef, useState } from "react"
import type { LayersConfig } from "../use-map/use-map"
import type { MapboxGeoJSONFeature, Map, MapLayerMouseEvent } from "mapbox-gl"

type Feature = MapboxGeoJSONFeature | null

export type MapFeatureEventsState = {
  clickedFeature: MapboxGeoJSONFeature | null
  clickedFeatureEventId: number
  hoveredFeature: MapboxGeoJSONFeature | null
}

export const getMapFeatureKey = (feature: Feature) => {
  if (!feature) return null

  return [feature.layer.source, feature.layer["source-layer"], feature.id].join("::")
}

export const isSameMapFeature = (a: Feature, b: Feature) =>
  getMapFeatureKey(a) === getMapFeatureKey(b)

const getInteractionLayerIds = (
  layers: LayersConfig["layers"],
  interaction: "clickable" | "hoverable"
) => layers.filter((layer) => layer[interaction]).map((layer) => layer.id)

/**
 * Listens to click and hover mouse events on the configured layers and returns
 * the hovered and clicked features.  Does not change Mapbox feature-state.
 * Consumers decide what hover/click means for their map e.g. selection
 * clickedFeatureEventId increments on every click so consumers can react to the click event
 * itself — useful when clickedFeature hasn't changed but a side effect still needs to fire.
 */
export function useMapFeatureEvents({
  layers,
  map,
}: {
  layers: LayersConfig["layers"]
  map: Map | null
}): MapFeatureEventsState {
  const [clickedFeature, setClickedFeature] = useState<MapboxGeoJSONFeature | null>(null)
  const [clickedFeatureEventId, setClickedFeatureEventId] = useState(0)
  const [hoveredFeature, setHoveredFeature] = useState<MapboxGeoJSONFeature | null>(null)

  const hoveredFeatureRef = useRef<Feature>(null)

  const clickableLayerKey = JSON.stringify(getInteractionLayerIds(layers, "clickable"))
  const hoverableLayerKey = JSON.stringify(getInteractionLayerIds(layers, "hoverable"))

  useEffect(() => {
    if (!map) return

    const clickableLayerIds = JSON.parse(clickableLayerKey) as string[]
    const hoverableLayerIds = JSON.parse(hoverableLayerKey) as string[]

    const setNextHoveredFeature = (feature: Feature) => {
      if (isSameMapFeature(feature, hoveredFeatureRef.current)) return

      hoveredFeatureRef.current = feature
      setHoveredFeature(feature)
    }

    const onClick = (e: MapLayerMouseEvent) => {
      if (!e.features?.length) return

      setClickedFeature(e.features[0]!)
      setClickedFeatureEventId((eventId) => eventId + 1)
    }

    const onMouseLeave = () => {
      setNextHoveredFeature(null)
    }

    const onMouseMove = (e: MapLayerMouseEvent) => {
      setNextHoveredFeature(e.features?.[0] ?? null)
    }

    if (clickableLayerIds.length) map.on("click", clickableLayerIds, onClick)

    if (hoverableLayerIds.length) {
      map.on("mousemove", hoverableLayerIds, onMouseMove)
      map.on("mouseleave", hoverableLayerIds, onMouseLeave)
    }

    return () => {
      if (clickableLayerIds.length) map.off("click", clickableLayerIds, onClick)

      if (hoverableLayerIds.length) {
        map.off("mousemove", hoverableLayerIds, onMouseMove)
        map.off("mouseleave", hoverableLayerIds, onMouseLeave)
      }

      hoveredFeatureRef.current = null
    }
  }, [map, clickableLayerKey, hoverableLayerKey])

  return {
    clickedFeature,
    clickedFeatureEventId,
    hoveredFeature,
  }
}
